"use client";

import React from "react";
import dynamic from "next/dynamic";

// These modals are mounted globally on every route but render null until their
// entry is opened via the modal stack. Loading their code with next/dynamic
// keeps it out of the shared client bundle shipped on first paint — the chunk
// is fetched lazily after hydration and the closed render is still null, so
// there is no behavior change.
const ComposeModalContainer = dynamic(
  () =>
    import("@/components/crm/messages/compose/ComposeModalContainer").then(
      (m) => m.ComposeModalContainer
    ),
  { ssr: false }
);
const CreateContactModal = dynamic(
  () => import("@/features/contacts/components/CreateContactModal").then((m) => m.CreateContactModal),
  { ssr: false }
);
const CreateCompanyModal = dynamic(
  () => import("@/features/companies/components/CreateCompanyModal").then((m) => m.CreateCompanyModal),
  { ssr: false }
);
const CreateDealModal = dynamic(
  () => import("@/features/deals/components/CreateDealModal").then((m) => m.CreateDealModal),
  { ssr: false }
);
const CreateOfferingModal = dynamic(
  () => import("@/features/offerings/components/CreateOfferingModal").then((m) => m.CreateOfferingModal),
  { ssr: false }
);
const CreateTimeEntryModal = dynamic(
  () =>
    import("@/features/time-entries/components/CreateTimeEntryModal").then(
      (m) => m.CreateTimeEntryModal
    ),
  { ssr: false }
);
const CreateTicketModal = dynamic(
  () => import("@/features/tickets/components/CreateTicketModal").then((m) => m.CreateTicketModal),
  { ssr: false }
);
const GrantEmployeeAccessModalWrapper = dynamic(
  () =>
    import("@/features/contacts/components/GrantEmployeeAccessModalWrapper").then(
      (m) => m.GrantEmployeeAccessModalWrapper
    ),
  { ssr: false }
);
const UserGrantClientAccessModal = dynamic(
  () =>
    import("@/components/admin/access/UserGrantAccessModals").then(
      (m) => m.UserGrantClientAccessModal
    ),
  { ssr: false }
);
const UserGrantEmployeeAccessModal = dynamic(
  () =>
    import("@/components/admin/access/UserGrantAccessModals").then(
      (m) => m.UserGrantEmployeeAccessModal
    ),
  { ssr: false }
);
const UserGrantDriveAccessModal = dynamic(
  () =>
    import("@/components/admin/access/UserGrantAccessModals").then(
      (m) => m.UserGrantDriveAccessModal
    ),
  { ssr: false }
);
const UnifiedTaskCreationModal = dynamic(
  () => import("./UnifiedTaskCreationModal").then((m) => m.UnifiedTaskCreationModal),
  { ssr: false }
);
const SendToSlackModal = dynamic(
  () => import("./SendToSlackModal").then((m) => m.SendToSlackModal),
  { ssr: false }
);
const SendTextMessageModal = dynamic(
  () => import("./SendTextMessageModal").then((m) => m.SendTextMessageModal),
  { ssr: false }
);
const ConfigureAutomatedTasksModalWrapper = dynamic(
  () =>
    import("./ConfigureAutomatedTasksModalWrapper").then(
      (m) => m.ConfigureAutomatedTasksModalWrapper
    ),
  { ssr: false }
);
const ConfigureCommunicationsGoalsModalWrapper = dynamic(
  () =>
    import("./ConfigureCommunicationsGoalsModalWrapper").then(
      (m) => m.ConfigureCommunicationsGoalsModalWrapper
    ),
  { ssr: false }
);
const CreateCommunicationModal = dynamic(
  () =>
    import("@/features/tasks/components/CreateCommunicationModal").then(
      (m) => m.CreateCommunicationModal
    ),
  { ssr: false }
);
const EnrollSupportGroupModal = dynamic(
  () =>
    import("@/features/companies/support-inbox/EnrollSupportGroupModal").then(
      (m) => m.EnrollSupportGroupModal
    ),
  { ssr: false }
);

/**
 * Global Quick Action Modals
 *
 * Renders all modals that can be opened from the quick actions dropdown.
 * These modals are available on every page via the global modal stack.
 *
 * Modals included:
 * - Compose Email (messages.compose)
 * - Create Task (tasks.create)
 * - Create Ticket (tickets.createQuick)
 * - Create Contact (contacts.createQuick)
 * - Create Company (companies.create)
 * - Create Deal (deals.create)
 * - Create Offering (offerings.create)
 * - Create Time Entry (timeEntries.createQuick)
 * - Grant Employee Access (employees.grantAccess)
 * - Send to Slack (send-to-slack)
 * - Send Text Message (send-text-message)
 * - Create Meeting (meetings.create) - handled by @wtc/ui global modal stack
 *
 * This component should be rendered in the root layout so these modals
 * can be opened from anywhere in the app via openModal(MODAL_IDS.xxx)
 */
export function GlobalQuickActionModals() {
  return (
    <>
      {/* Compose Email Modal */}
      <ComposeModalContainer />

      {/* Create Task Modal */}
      <UnifiedTaskCreationModal />

      {/* Create Contact Modal */}
      <CreateContactModal />

      {/* Create Company Modal */}
      <CreateCompanyModal />

      {/* Create Deal Modal */}
      <CreateDealModal />

      {/* Create Offering Modal */}
      <CreateOfferingModal />

      {/* Create Ticket Modal */}
      <CreateTicketModal />

      {/* Create Time Entry Modal */}
      <CreateTimeEntryModal />

      {/* Create Communication Modal */}
      <CreateCommunicationModal />

      {/* Grant Employee Access Modal (CONTACT-centric: share a contact with employees) */}
      <GrantEmployeeAccessModalWrapper />

      {/* User-centric access grants (a user is granted access to contacts) */}
      <UserGrantClientAccessModal />
      <UserGrantEmployeeAccessModal />

      {/* Lend a Google Drive connection to a Drive-less admin */}
      <UserGrantDriveAccessModal />

      {/* Send to Slack Modal */}
      <SendToSlackModal />

      {/* Send Text Message Modal */}
      <SendTextMessageModal />

      {/* Automated follow-up tasks modal */}
      <ConfigureAutomatedTasksModalWrapper />

      {/* Weekly communications goals modal */}
      <ConfigureCommunicationsGoalsModalWrapper />

      {/* Enroll Google support group for a company */}
      <EnrollSupportGroupModal />

      {/* Create Meeting Modal is handled by @west-tennessee-consulting/ui's global modal stack */}
    </>
  );
}
